import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  Refresh,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { apiConfig } from '../config/api';

const AdminGames = () => {
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [formData, setFormData] = useState({
    homeTeamId: '',
    awayTeamId: '',
    gameDate: new Date(),
    season: '2024-25',
    round: 1,
    homeScore: '',
    awayScore: '',
    status: 'scheduled'
  });

  useEffect(() => {
    fetchGames();
    fetchTeams();
  }, []);

  const fetchGames = async () => {
    try {
      const response = await axios.get(apiConfig.admin.games);
      setGames(response.data.games);
    } catch (error) {
      setError('Failed to fetch games');
      console.error('Error fetching games:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(apiConfig.admin.teams);
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingGame) {
        await axios.put(apiConfig.admin.game(editingGame._id), formData);
      } else {
        await axios.post(apiConfig.admin.games, formData);
      }
      setOpenDialog(false);
      setEditingGame(null);
      fetchGames();
      resetForm();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save game');
    }
  };

  const handleDelete = async (gameId) => {
    if (window.confirm('Are you sure you want to delete this game?')) {
      try {
        await axios.delete(apiConfig.admin.game(gameId));
        fetchGames();
      } catch (error) {
        setError('Failed to delete game');
      }
    }
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    setFormData({
      homeTeamId: game.homeTeam._id,
      awayTeamId: game.awayTeam._id,
      gameDate: new Date(game.gameDate),
      season: game.season,
      round: game.round,
      homeScore: game.homeScore || '',
      awayScore: game.awayScore || '',
      status: game.status
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      homeTeamId: '',
      awayTeamId: '',
      gameDate: new Date(),
      season: '2024-25',
      round: 1,
      homeScore: '',
      awayScore: '',
      status: 'scheduled'
    });
  };

  const recalculatePoints = async () => {
    try {
      setLoading(true);
      await axios.post(apiConfig.admin.recalculatePoints);
      alert('Points recalculated successfully!');
    } catch (error) {
      setError('Failed to recalculate points');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'finished': return 'success';
      case 'live': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">Games Management</Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={recalculatePoints}
              sx={{ mr: 2 }}
            >
              Recalculate Points
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                resetForm();
                setOpenDialog(true);
              }}
            >
              Add Game
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Home Team</TableCell>
                <TableCell>Away Team</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {games.map((game) => (
                <TableRow key={game._id}>
                  <TableCell>
                    {new Date(game.gameDate).toLocaleString('fi-FI')}
                  </TableCell>
                  <TableCell>{game.homeTeam.name}</TableCell>
                  <TableCell>{game.awayTeam.name}</TableCell>
                  <TableCell>
                    {game.homeScore !== undefined && game.awayScore !== undefined
                      ? `${game.homeScore} - ${game.awayScore}`
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={game.status}
                      color={getStatusColor(game.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEdit(game)} size="small">
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(game._id)} size="small" color="error">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Game Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingGame ? 'Edit Game' : 'Add New Game'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Home Team"
                  value={formData.homeTeamId}
                  onChange={(e) => setFormData({ ...formData, homeTeamId: e.target.value })}
                >
                  {teams.map((team) => (
                    <MenuItem key={team._id} value={team._id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Away Team"
                  value={formData.awayTeamId}
                  onChange={(e) => setFormData({ ...formData, awayTeamId: e.target.value })}
                >
                  {teams.map((team) => (
                    <MenuItem key={team._id} value={team._id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Game Date"
                  value={formData.gameDate}
                  onChange={(date) => setFormData({ ...formData, gameDate: date })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="live">Live</MenuItem>
                  <MenuItem value="finished">Finished</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Season"
                  value={formData.season}
                  onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Round"
                  type="number"
                  value={formData.round}
                  onChange={(e) => setFormData({ ...formData, round: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Home Score"
                  type="number"
                  value={formData.homeScore}
                  onChange={(e) => setFormData({ ...formData, homeScore: parseInt(e.target.value) || '' })}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Away Score"
                  type="number"
                  value={formData.awayScore}
                  onChange={(e) => setFormData({ ...formData, awayScore: parseInt(e.target.value) || '' })}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} startIcon={<Cancel />}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" startIcon={<Save />}>
              {editingGame ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminGames;
